import { useEffect, useRef, useState } from 'react';
import type { Rarity } from '@/domain/content.types';
import { useT } from '@/i18n/useT';
import { useSettings, type StatusFilter } from '@/state/settingsStore';

const RARITIES: Rarity[] = ['guaranteed', 'common', 'rare', 'ultra-rare', 'legendary'];

interface FilterBarProps {
  counts: { all: number; done: number; todo: number };
}

export function FilterBar({ counts }: FilterBarProps) {
  const { t } = useT();
  // Rarity chips are a whole extra row on a phone, where the sticky header is
  // already competing with the list. Hidden behind a toggle below `md`, always
  // visible above it.
  const [rarityOpen, setRarityOpen] = useState(false);
  const filters = useSettings((s) => s.filters);
  const setFilters = useSettings((s) => s.setFilters);
  const resetFilters = useSettings((s) => s.resetFilters);
  const isDefault = useSettings((s) => s.filtersAreDefault)();
  const searchRef = useRef<HTMLInputElement>(null);

  // "/" focuses search, Escape clears it — muscle memory from every other
  // list-heavy tool.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'Escape' && target === searchRef.current) {
        setFilters({ search: '' });
        searchRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setFilters]);

  const statuses: { key: StatusFilter; label: string; count: number }[] = [
    { key: 'all', label: t('filter.all'), count: counts.all },
    { key: 'done', label: t('filter.done'), count: counts.done },
    { key: 'todo', label: t('filter.todo'), count: counts.todo },
  ];

  const toggleRarity = (rarity: Rarity): void => {
    setFilters({
      rarities: filters.rarities.includes(rarity)
        ? filters.rarities.filter((r) => r !== rarity)
        : [...filters.rarities, rarity],
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-line bg-bg/95 px-2 py-1.5 backdrop-blur">
      <div className="relative min-w-40 flex-1 md:max-w-72">
        <input
          ref={searchRef}
          type="search"
          value={filters.search}
          onChange={(e) => setFilters({ search: e.target.value })}
          placeholder={t('filter.search')}
          aria-label={t('filter.searchAria')}
          className="w-full rounded-xs border border-line bg-surface px-2 py-1 text-[13px] leading-5 text-text placeholder:text-text-faint focus:border-pip-dim focus:outline-none"
        />
      </div>

      <div className="flex gap-1" role="group" aria-label={t('filter.all')}>
        {statuses.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setFilters({ status: s.key })}
            aria-pressed={filters.status === s.key}
            className={`tnum rounded-xs border px-2 py-1 text-[12px] leading-4 transition-colors ${
              filters.status === s.key
                ? 'border-pip-dim bg-pip-faint/30 text-pip'
                : 'border-line bg-surface text-text-dim hover:border-pip-dim/60'
            }`}
          >
            {s.label} <span className="text-text-faint">{s.count}</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setRarityOpen((v) => !v)}
        aria-expanded={rarityOpen}
        className={`rounded-xs border px-2 py-1 text-[11px] leading-4 md:hidden ${
          filters.rarities.length > 0
            ? 'border-pip-dim bg-pip-faint/30 text-pip'
            : 'border-line bg-surface text-text-faint'
        }`}
      >
        {t('filter.rarity')}
        {filters.rarities.length > 0 ? ` ${filters.rarities.length}` : ''}
      </button>

      <div
        className={`${rarityOpen ? 'flex' : 'hidden'} w-full flex-wrap gap-1 md:flex md:w-auto`}
        role="group"
        aria-label={t('filter.rarity')}
      >
        {RARITIES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => toggleRarity(r)}
            aria-pressed={filters.rarities.includes(r)}
            className={`rounded-xs border px-1.5 py-1 text-[11px] leading-4 transition-colors ${
              filters.rarities.includes(r)
                ? 'border-pip-dim bg-pip-faint/30 text-pip'
                : 'border-line bg-surface text-text-faint hover:border-pip-dim/60'
            }`}
          >
            {t(`rarity.${r}` as const)}
          </button>
        ))}
      </div>

      {/* Always offer an escape hatch when a filter is hiding rows — otherwise
          an empty list reads as a bug. */}
      {isDefault ? null : (
        <button
          type="button"
          onClick={resetFilters}
          className="rounded-xs border border-amber/40 px-2 py-1 text-[11px] leading-4 text-amber hover:bg-amber/10"
        >
          {t('filter.reset')}
        </button>
      )}
    </div>
  );
}
